import React, { useState, KeyboardEvent } from 'react';
import { Input } from 'components/ui/input';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../../components/core/theme-provider';


interface UserSelectorProps {
    onEmailsChange?: (emails: string[]) => void;
    maxEmails?: number;
}

export const UserSelector: React.FC<UserSelectorProps> = ({ onEmailsChange, maxEmails }) => {
    const { t } = useTranslation();
    const [emailInput, setEmailInput] = useState('');
    const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
    const [validationError, setValidationError] = useState<string>('');
    const { theme } = useTheme();
    const isValidEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && emailInput.trim()) {
            e.preventDefault();

            const email = emailInput.trim().toLowerCase();

            if (!isValidEmail(email)) {
                setValidationError(t('INVALID_EMAIL_FORMAT'));
                return;
            }

            if (selectedEmails.includes(email)) {
                setValidationError(t('EMAIL_ALREADY_ADDED'));
                return;
            }

            if (maxEmails && selectedEmails.length >= maxEmails) {
                setValidationError(t('MAX_USERS_LIMIT_REACHED'));
                return;
            }

            const newSelectedEmails = [...selectedEmails, email];
            setSelectedEmails(newSelectedEmails);
            onEmailsChange?.(newSelectedEmails);
            setEmailInput('');
            setValidationError('');
        }
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmailInput(e.target.value);
        setValidationError(''); // Clear validation error when user starts typing
    };

    const handleEmailRemove = (emailToRemove: string) => {
        const newSelectedEmails = selectedEmails.filter(email => email !== emailToRemove);
        setSelectedEmails(newSelectedEmails);
        onEmailsChange?.(newSelectedEmails);
        setValidationError(''); // Clear validation error when removing an email
    };

    const isMaxReached = maxEmails ? selectedEmails.length >= maxEmails : false;

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-1.5">
                {/* <label className="text-foreground font-medium text-base">
                    {t('EMAIL_ADDRESS')}
                </label> */}
                <div className="space-y-2">
                    <div className="relative">
                        <Input
                            type="email"
                            value={emailInput}
                            onChange={handleEmailChange}
                            onKeyDown={handleKeyDown}
                            placeholder={isMaxReached ? t('MAX_USERS_REACHED') : t('TYPE_EMAIL_AND_PRESS_ENTER')}
                            className={`pl-9 ${validationError ? 'border-error' : ''}`}
                            disabled={isMaxReached}
                        />
                        <svg
                            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                        </svg>
                    </div>
                    <div className="flex justify-between items-center min-h-[20px]">
                        <div className="flex-1">
                            {emailInput && !validationError && (
                                <p className="text-sm text-warning">
                                    {t('TYPE_EMAIL_AND_PRESS_ENTER')}
                                </p>
                            )}
                        </div>
                        <div className="flex justify-end">
                            {validationError && (
                                <p className="text-sm text-error">{validationError}</p>
                            )}
                            {!validationError && maxEmails && (
                                <p className="text-sm text-medium-emphasis">
                                    {t('USERS_SELECTED')}: {selectedEmails.length}/{maxEmails}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {selectedEmails.length > 0 && (
                <div className="flex flex-col gap-1">
                    {/*  <label className="text-foreground font-medium text-sm">
                        {t('SELECTED_USERS')}
                    </label> */}
                    <div className="flex flex-wrap gap-1.5">
                        {selectedEmails.map(email => (
                            <div
                                key={email}
                                className={`flex items-center gap-2.5 ${theme === 'light' ? 'bg-muted' : 'bg-slate-800'} px-3 py-1.5 rounded`}
                            >
                                <span className="text-sm whitespace-nowrap text-foreground">{email}</span>
                                <button
                                    onClick={() => handleEmailRemove(email)}
                                    className="h-4 w-4 flex items-center justify-center"
                                >
                                    <svg
                                        width="12"
                                        height="12"
                                        viewBox="0 0 12 12"
                                        fill="none"
                                        stroke="currentColor"
                                        className="text-foreground"
                                    >
                                        <path
                                            d="M9 3L3 9M3 3L9 9"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}; 